// src/pages/admin/AdminPage.tsx - Main admin dashboard
import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  CloudArrowDownIcon,
  UsersIcon,
  ArrowLeftIcon,
  TrophyIcon,
  RectangleGroupIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { AdminOverview } from '../../types';
import { timeAgo } from '../../utils/timeAgo';

// Known statuses render in this order; anything else the API returns is
// appended so the card can never silently undercount again (it was dropping
// `playoffs` and the legacy `draft`).
const KNOWN_LEAGUE_STATUSES = ['setup', 'drafting', 'active', 'playoffs', 'completed'];

function orderedStatuses(counts: Record<string, number>): string[] {
  const extra = Object.keys(counts).filter((s) => !KNOWN_LEAGUE_STATUSES.includes(s));
  return [...KNOWN_LEAGUE_STATUSES, ...extra.sort()];
}

export default function AdminPage() {
  const { user } = useAuth();
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.is_superuser) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getAdminOverview();
        if (!cancelled) setOverview(data);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to load overview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.is_superuser]);

  if (!user?.is_superuser) {
    console.warn('Admin access denied — is_superuser is false');
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="space-y-6">
      {/* Masthead */}
      <div>
        <Link
          to="/app"
          className="inline-flex items-center text-sm font-semibold uppercase tracking-wide text-secondary-600 hover:text-ink-900 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-1.5" />
          Back to Dashboard
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1 border-b-2 border-ink-900 pb-3">
          <div>
            <h1 className="font-display uppercase text-ink-900 text-4xl leading-none">Admin Panel</h1>
            <p className="text-secondary-600 mt-1">Back-of-house. NFL data, systems, and league operations.</p>
          </div>
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-secondary-500">
            Superuser · {user?.username}
          </span>
        </div>
      </div>

      {/* Overview */}
      {loading && (
        <div className="flex items-center justify-center py-10">
          <div className="loading-spinner w-6 h-6 mr-3" />
          <span className="text-secondary-500 text-sm">Loading overview…</span>
        </div>
      )}
      {!loading && error && (
        <p className="text-sm text-danger py-4">{error}</p>
      )}
      {!loading && !error && overview && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          <div className="card">
            <div className="card-body text-center">
              <UsersIcon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
              <div className="font-display text-3xl text-ink-900">{overview.users.total}</div>
              <div className="text-xs uppercase tracking-wide text-secondary-500 mt-1">Total Users</div>
              <div className="text-xs text-secondary-400 mt-1">
                {overview.users.active} active · {overview.users.disabled} disabled
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body text-center">
              <TrophyIcon className="w-8 h-8 text-secondary-700 mx-auto mb-2" />
              <div className="text-xs uppercase tracking-wide text-secondary-500 mb-2">Leagues</div>
              <ul className="text-xs text-secondary-600 space-y-0.5">
                {orderedStatuses(overview.leagues).map((status) => (
                  <li key={status} className="flex justify-between">
                    <span className="capitalize" data-testid="league-status-label">{status}</span>
                    <span className="font-display text-ink-900">{overview.leagues[status] ?? 0}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="card">
            <div className="card-body text-center">
              <RectangleGroupIcon className="w-8 h-8 text-secondary-700 mx-auto mb-2" />
              <div className="font-display text-3xl text-ink-900">{overview.teams}</div>
              <div className="text-xs uppercase tracking-wide text-secondary-500 mt-1">Teams</div>
            </div>
          </div>

          <div className="card">
            <div className="card-body text-center">
              <ArrowTrendingUpIcon className="w-8 h-8 text-secondary-700 mx-auto mb-2" />
              <div className="font-display text-3xl text-ink-900">{overview.signups.last_7}</div>
              <div className="text-xs uppercase tracking-wide text-secondary-500 mt-1">Signups (7d)</div>
              <div className="text-xs text-secondary-400 mt-1">{overview.signups.last_30} in last 30d</div>
            </div>
          </div>

          {/* Signups say nothing about whether anyone came back. */}
          {overview.active && (
            <div className="card">
              <div className="card-body text-center">
                <UsersIcon className="w-8 h-8 text-secondary-700 mx-auto mb-2" />
                <div className="font-display text-3xl text-ink-900" data-testid="active-7d">
                  {overview.active.last_7}
                </div>
                <div className="text-xs uppercase tracking-wide text-secondary-500 mt-1">
                  Logged in (7d)
                </div>
                <div className="text-xs text-secondary-400 mt-1">
                  {overview.active.last_30} in 30d · {overview.active.never_logged_in} never
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Admin Tools */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* NFL Roster Management */}
        <Link to="/app/admin/roster-updates" className="card hover:shadow-lg transition-shadow group">
          <div className="card-body">
            <div className="flex items-center space-x-3 mb-4">
              <CloudArrowDownIcon className="w-8 h-8 text-primary-600 group-hover:text-primary-700" />
              <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">NFL Roster Updates</h3>
            </div>
            <p className="text-secondary-600 mb-4">
              Manage live NFL roster synchronization, view recent changes, and control background update services.
            </p>
            <div className="flex items-center justify-between">
              <span className="text-sm text-primary-600 group-hover:text-primary-700">
                Manage Roster Data →
              </span>
              <div className="w-2 h-2 bg-success-500 rounded-full"></div>
            </div>
          </div>
        </Link>

        {/* Users */}
        <Link to="/app/admin/users" data-testid="admin-users-link" className="card hover:shadow-lg transition-shadow group">
          <div className="card-body">
            <div className="flex items-center space-x-3 mb-4">
              <UserGroupIcon className="w-8 h-8 text-primary-600 group-hover:text-primary-700" />
              <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">Users</h3>
            </div>
            <p className="text-secondary-600 mb-4">
              Manage accounts — disable or re-enable access, promote or demote superusers, and remove clean accounts.
            </p>
            <span className="text-sm text-primary-600 group-hover:text-primary-700">Manage Users →</span>
          </div>
        </Link>

        {/* Leagues */}
        <Link to="/app/admin/leagues" data-testid="admin-leagues-link" className="card hover:shadow-lg transition-shadow group">
          <div className="card-body">
            <div className="flex items-center space-x-3 mb-4">
              <TrophyIcon className="w-8 h-8 text-primary-600 group-hover:text-primary-700" />
              <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">Leagues</h3>
            </div>
            <p className="text-secondary-600 mb-4">
              Every league on the site, with flags for the ones stalled in setup, missing a schedule, or overdue for scoring.
            </p>
            <span className="text-sm text-primary-600 group-hover:text-primary-700">View Leagues →</span>
          </div>
        </Link>

        {/* Cleanup */}
        <Link to="/app/admin/cleanup" data-testid="admin-cleanup-link" className="card hover:shadow-lg transition-shadow group">
          <div className="card-body">
            <div className="flex items-center space-x-3 mb-4">
              <WrenchScrewdriverIcon className="w-8 h-8 text-primary-600 group-hover:text-primary-700" />
              <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">Cleanup</h3>
            </div>
            <p className="text-secondary-600 mb-4">
              Delete abandoned leagues and purge placeholder stat rows. Destructive — used sparingly.
            </p>
            <span className="text-sm text-primary-600 group-hover:text-primary-700">Run Cleanup →</span>
          </div>
        </Link>

        {/* Stats */}
        <Link to="/app/admin/stats" data-testid="admin-stats-link" className="card hover:shadow-lg transition-shadow group">
          <div className="card-body">
            <div className="flex items-center space-x-3 mb-4">
              <ChartBarIcon className="w-8 h-8 text-primary-600 group-hover:text-primary-700" />
              <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">Stats</h3>
            </div>
            <p className="text-secondary-600 mb-4">
              Manually trigger stat ingestion for a season/week, or rebuild the player ID map.
            </p>
            <span className="text-sm text-primary-600 group-hover:text-primary-700">Manage Stats →</span>
          </div>
        </Link>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">Recent Admin Activity</h3>
        </div>
        {loading ? (
          <div className="card-body flex items-center justify-center py-6">
            <div className="loading-spinner w-4 h-4 mr-2" />
            <span className="text-secondary-500 text-sm">Loading…</span>
          </div>
        ) : error ? (
          <div className="card-body py-6 text-center text-sm text-danger">{error}</div>
        ) : !overview || overview.activity.length === 0 ? (
          <div className="card-body py-6 text-center text-secondary-500 text-sm">No recent activity.</div>
        ) : (
          <div className="divide-y divide-secondary-100">
            {overview.activity.map((entry, i) => (
              <div key={`${entry.kind}-${entry.ts}-${i}`} className="flex items-center gap-3 px-5 py-3">
                <span className="font-display uppercase text-[10px] tracking-wider px-1.5 py-0.5 shrink-0 bg-ink-900 text-gold">
                  {entry.kind.toUpperCase()}
                </span>
                <span className="flex-1 min-w-0 text-sm text-secondary-900 truncate">{entry.label}</span>
                <span className="text-xs text-secondary-400 shrink-0">{timeAgo(entry.ts)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
